import React from 'react';
import { getTheme, Icon, Link, PrimaryButton, ProgressIndicator, Spinner, SpinnerSize, Stack } from '@fluentui/react';
import { IGettingStartedStep, IGettingStartedStepItem } from '../../models/GettingStarted';
import {
    ADD_ANOTHER_LABEL,
    CATEGORY_ORDER,
    CHECKLIST_STRINGS,
    getCategoryStrings,
    getStepCtaLabel,
    getStepDescription,
    getStepStrings,
    ITEM_STATUS_STRINGS,
    STATUS_STRINGS
} from './wizardStrings';

// The checklist content, and nothing else.
//
// This component is deliberately container agnostic: it knows nothing about the Panel it currently
// lives in, so the same markup can be dropped into a Callout / popover later without touching it.
// It also owns no data fetching and no navigation. Steps and a per-step action resolver come in as
// props; the parent keeps the step-id to action map.

export interface IGettingStartedStepAction {
    /** Button or link text. Defaults to the step's status-aware CTA copy when omitted. */
    label?: string;
    onClick: () => void;
    /** Render as a filled button rather than a link. Used for `needsReview` nudges. */
    isProminent?: boolean;
}

export interface IGettingStartedChecklistProps {
    steps: IGettingStartedStep[];
    /** Resolves the action for a step. Return undefined for steps with nothing to click. */
    getStepAction?: (step: IGettingStartedStep) => IGettingStartedStepAction | undefined;
    /**
     * Opens one of the connections listed under a multi-part step. Omit it and the connections still
     * render, they just are not clickable.
     */
    onOpenStepItem?: (step: IGettingStartedStep, item: IGettingStartedStepItem) => void;
    isLoading?: boolean;
    /** Optional heading/intro suppression for hosts that supply their own chrome. */
    hideHeader?: boolean;
}

const theme = getTheme();

const isClickableStatus = (step: IGettingStartedStep) => step.status !== 'locked' && step.status !== 'complete';

/** The glyph in the left gutter of a row. One place to change the status vocabulary. */
const StepStatusIndicator: React.FC<{ step: IGettingStartedStep }> = ({ step }) => {
    const size = { width: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' } as React.CSSProperties;
    switch (step.status) {
        case 'complete':
            return (
                <div style={size}>
                    <Icon iconName="CompletedSolid" style={{ fontSize: '18px', color: theme.palette.themePrimary }} />
                </div>
            );
        case 'processing':
            return (
                <div style={size}>
                    <Spinner size={SpinnerSize.small} />
                </div>
            );
        case 'needsReview':
            return (
                <div style={size}>
                    <span
                        aria-hidden
                        style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            backgroundColor: theme.palette.orangeLighter,
                            display: 'inline-block'
                        }}
                    />
                </div>
            );
        case 'locked':
            return (
                <div style={size}>
                    <Icon iconName="Lock" style={{ fontSize: '16px', color: theme.palette.neutralTertiary }} />
                </div>
            );
        default:
            return (
                <div style={size}>
                    <Icon iconName="ChevronRightMed" style={{ fontSize: '14px', color: theme.palette.themePrimary }} />
                </div>
            );
    }
};

/** The dot beside one connection. Deliberately quieter than the step's own gutter glyph. */
const ItemStatusDot: React.FC<{ status: IGettingStartedStepItem['status'] }> = ({ status }) => {
    const color = status === 'complete'
        ? theme.palette.green
        : status === 'needsReview'
            ? theme.palette.redDark
            : theme.palette.neutralTertiary;
    return (
        <span
            aria-hidden
            style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: color,
                display: 'inline-block',
                flexShrink: 0,
                marginTop: '6px'
            }}
        />
    );
};

/**
 * One connection under a step. Its own status line is the point of the row: the step above can only
 * say that something is unfinished, not which one.
 */
const StepItemRow: React.FC<{
    item: IGettingStartedStepItem,
    onClick?: () => void
}> = ({ item, onClick }) => {
    const statusLabel = ITEM_STATUS_STRINGS[item.status as keyof typeof ITEM_STATUS_STRINGS];
    return (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5em', padding: '0.25em 0' }}>
            <ItemStatusDot status={item.status} />
            <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '13px' }}>
                    {onClick ? <Link onClick={onClick}>{item.name}</Link> : item.name}
                </div>
                <div
                    style={{
                        fontSize: '12px',
                        color: item.status === 'needsReview' ? theme.palette.redDark : theme.palette.neutralSecondary
                    }}
                >
                    {/* The connector's own words when it rejected the credentials; our label otherwise. */}
                    {(item.status === 'needsReview' && item.message) || statusLabel}
                </div>
            </div>
        </div>
    );
};

const StepRow: React.FC<{
    step: IGettingStartedStep,
    action?: IGettingStartedStepAction,
    onOpenItem?: (item: IGettingStartedStepItem) => void
}> = ({ step, action, onOpenItem }) => {
    const strings = getStepStrings(step.id);
    const description = getStepDescription(step.id, step.status);
    const items = step.items ?? [];
    const complete = step.status === 'complete';
    // A step that tracks several connections is never finished with the user: they can add one more
    // after it has gone green, so its action survives `complete` where a single-outcome step's does not.
    const label = action?.label ?? (complete ? ADD_ANOTHER_LABEL : getStepCtaLabel(step.id, step.status));
    const clickable = !!action && (isClickableStatus(step) || items.length > 0);

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5em',
                padding: '0.5em 0',
                borderTop: `1px solid ${theme.palette.neutralLighter}`,
                opacity: step.status === 'locked' ? 0.6 : 1
            }}
        >
            <StepStatusIndicator step={step} />
            <div style={{ flexGrow: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, textDecoration: complete ? 'line-through' : undefined, color: complete ? theme.palette.neutralSecondary : theme.palette.neutralPrimary }}>
                    {clickable
                        ? <Link onClick={action?.onClick}>{strings.title}</Link>
                        : strings.title}
                </div>
                {description && (
                    <div style={{ color: theme.palette.neutralSecondary, fontSize: '12px', marginTop: '2px' }}>
                        {description}
                    </div>
                )}
                {!clickable && !complete && (
                    <div style={{ color: theme.palette.neutralTertiary, fontSize: '12px', marginTop: '2px' }}>
                        {STATUS_STRINGS[step.status]}
                    </div>
                )}
                {items.length > 0 && (
                    <div style={{ marginTop: '0.5em' }}>
                        {items.map(item => (
                            <StepItemRow
                                key={item.id}
                                item={item}
                                onClick={onOpenItem ? () => onOpenItem(item) : undefined}
                            />
                        ))}
                    </div>
                )}
                {clickable && action && (
                    <div style={{ marginTop: '0.5em' }}>
                        {action.isProminent
                            ? <PrimaryButton text={label} onClick={action.onClick} />
                            : <Link onClick={action.onClick}>{label}</Link>}
                    </div>
                )}
            </div>
        </div>
    );
};

const GettingStartedChecklist: React.FC<IGettingStartedChecklistProps> = (props) => {
    const { steps, getStepAction, onOpenStepItem, isLoading, hideHeader } = props;

    const completeCount = steps.filter(s => s.status === 'complete').length;
    const total = steps.length;
    const allComplete = total > 0 && completeCount === total;

    // Known categories first, in the order wizardStrings declares them, then anything the API sent
    // that this build does not know about yet.
    const categories = React.useMemo(() => {
        const seen: string[] = [];
        steps.forEach(step => {
            if (!seen.includes(step.category)) {
                seen.push(step.category);
            }
        });
        const known = CATEGORY_ORDER.filter(c => seen.includes(c));
        const unknown = seen.filter(c => !CATEGORY_ORDER.includes(c));
        return [...known, ...unknown];
    }, [steps]);

    if (isLoading && total === 0) {
        return <Spinner label={CHECKLIST_STRINGS.loading} />;
    }

    if (total === 0) {
        return <div style={{ color: theme.palette.neutralSecondary }}>{CHECKLIST_STRINGS.emptyMessage}</div>;
    }

    return (
        <Stack tokens={{ childrenGap: '1em' }}>
            {!hideHeader && (
                <Stack tokens={{ childrenGap: '0.5em' }}>
                    <div style={{ color: theme.palette.neutralSecondary }}>
                        {allComplete ? CHECKLIST_STRINGS.allCompleteDescription : CHECKLIST_STRINGS.panelSubtitle}
                    </div>
                    <ProgressIndicator
                        label={CHECKLIST_STRINGS.progressLabel(completeCount, total)}
                        percentComplete={completeCount / total}
                    />
                </Stack>
            )}
            {categories.map(category => {
                const categoryStrings = getCategoryStrings(category);
                const categorySteps = steps.filter(s => s.category === category);
                return (
                    <div key={category}>
                        <div style={{ fontWeight: 600, fontSize: '14px' }}>{categoryStrings.title}</div>
                        {categoryStrings.description && (
                            <div style={{ color: theme.palette.neutralSecondary, fontSize: '12px' }}>
                                {categoryStrings.description}
                            </div>
                        )}
                        <div style={{ marginTop: '0.5em' }}>
                            {categorySteps.map(step => (
                                <StepRow
                                    key={step.id}
                                    step={step}
                                    action={getStepAction ? getStepAction(step) : undefined}
                                    onOpenItem={onOpenStepItem ? (item) => onOpenStepItem(step, item) : undefined}
                                />
                            ))}
                        </div>
                    </div>
                );
            })}
        </Stack>
    );
};

export default GettingStartedChecklist;
